import React, { useState } from 'react';
import { Calendar, CheckCircle2, Loader2 } from 'lucide-react';
import { submitWeb3Form } from '../../services/web3Forms';
import { Modal, Input, Textarea, Select, Button } from '../ui';
import { appointmentServices } from '../../data/appointmentServices';

export interface AppointmentModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const AppointmentModal: React.FC<AppointmentModalProps> = ({ isOpen, onClose }) => {
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    company: '',
    service: appointmentServices[0],
    preferredDate: '',
    preferredTime: '10:00 AM',
    message: '',
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await submitWeb3Form('appointment', {
        fullName: formData.fullName,
        email: formData.email,
        phone: formData.phone,
        company: formData.company,
        service: formData.service,
        preferredDate: formData.preferredDate,
        preferredTime: formData.preferredTime,
        message: formData.message,
        subject: `New Tech Strategy Call Booking from ${formData.fullName}`,
      });
    } catch (err) {
      console.log('Web3Forms submission handled:', err);
    } finally {
      setIsSubmitting(false);
      setSubmitted(true);
    }
  };

  const handleReset = () => {
    setSubmitted(false);
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Book a 1-on-1 Tech Strategy Session">
      {submitted ? (
        <div className="text-center py-8 space-y-4">
          <div className="w-16 h-16 rounded-full bg-emerald-500/20 text-emerald-600 dark:text-emerald-400 flex items-center justify-center mx-auto border border-emerald-500/30">
            <CheckCircle2 size={36} />
          </div>
          <h4 className="text-xl font-bold text-[#184166] dark:text-white">Appointment Scheduled!</h4>
          <p className="text-xs text-slate-600 dark:text-slate-300 max-w-sm mx-auto">
            Thank you, {formData.fullName}. Our lead team at Imperial Plaza, Parañaque will confirm your consultation details via email ({formData.email}) and phone.
          </p>
          <Button variant="primary" onClick={handleReset} className="mx-auto">
            Close Dialog
          </Button>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-4">
          <p className="text-xs text-slate-600 dark:text-slate-400">
            Schedule a direct consultation with Technook’s senior software architects and strategy leads.
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Input
              label="Full Name *"
              required
              value={formData.fullName}
              onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
              placeholder="e.g. Maria Santos"
            />
            <Input
              label="Work Email *"
              type="email"
              required
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              placeholder="maria@company.com"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Input
              label="Phone / Viber *"
              required
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              placeholder="+63 9XX XXX XXXX"
            />
            <Input
              label="Company Name"
              value={formData.company}
              onChange={(e) => setFormData({ ...formData, company: e.target.value })}
              placeholder="Acme Corp"
            />
          </div>

          <Select
            label="Service Area of Interest *"
            value={formData.service}
            onChange={(e) => setFormData({ ...formData, service: e.target.value })}
            options={appointmentServices.map((s) => ({ label: s, value: s }))}
          />

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Input
              label="Preferred Date *"
              type="date"
              required
              value={formData.preferredDate}
              onChange={(e) => setFormData({ ...formData, preferredDate: e.target.value })}
            />
            <Select
              label="Preferred Time Slot *"
              value={formData.preferredTime}
              onChange={(e) => setFormData({ ...formData, preferredTime: e.target.value })}
              options={[
                { label: '10:00 AM (PHT)', value: '10:00 AM' },
                { label: '02:00 PM (PHT)', value: '02:00 PM' },
                { label: '04:00 PM (PHT)', value: '04:00 PM' },
              ]}
            />
          </div>

          <Textarea
            label="Project Notes / Requirements"
            rows={3}
            value={formData.message}
            onChange={(e) => setFormData({ ...formData, message: e.target.value })}
            placeholder="Tell us briefly about your application goals or current tech stack..."
          />

          <Button type="submit" variant="primary" className="w-full" disabled={isSubmitting}>
            {isSubmitting ? <Loader2 size={16} className="animate-spin" /> : <Calendar size={16} />}
            <span>{isSubmitting ? 'Submitting Schedule...' : 'Confirm Booking Request'}</span>
          </Button>
        </form>
      )}
    </Modal>
  );
};
